import { useEffect, useState } from 'react'
import { message } from 'antd'
import { ALLOWED_ATTACHMENT_SOURCE_TYPE } from '@OpsQuality/constants'
import { UploadUIType } from '@OpsQuality/types'
import { adminJobRequestQuery_jobRequest_parts_linesWithWorkOrderMapping_jobRequestLineAttachments as JobRequestAttachmentType } from '@OpsQuality/JobRequestDetails/__generated__/adminJobRequestQuery'
import { transformFileToJobRequestAttachment } from '@OpsQuality/logic'
import { MutationOperations } from '@OpsQuality/JobRequestDetails/components/logic'

import Box from '~/core/Box'
import logger from '~/lib/logger'
import getColor from '~/lib/getColor'
import UploadComponent from '~/components/FileUploadList/components/UploadComponent'
import FileList from '~/components/FileUploadList'

import styles from './styles.css'

export default function UploadUI(props: UploadUIType) {
    const {
        selectedJobRequestLines,
        attachmentTypeDetails,
        jobRequestId,
        multiple,
        uploadTitle,
        attachmentSource,
        isJobCompleted,
        setCheckboxesLocked,
    } = props

    const { attachmentTypeData } = attachmentTypeDetails
    const { title } = attachmentTypeData

    let jobRequestAttachmentsData: JobRequestAttachmentType[] = []

    if (attachmentSource === ALLOWED_ATTACHMENT_SOURCE_TYPE.MP) {
        jobRequestAttachmentsData = selectedJobRequestLines
            .map(item => item.jobRequestLineAttachments)
            .flat()
    }
    const tempArray: JobRequestAttachmentType[] = jobRequestAttachmentsData

    const mutatingOperation = MutationOperations()

    const [jobRequestAttachments, setJobRequestAttachments] = useState<
        JobRequestAttachmentType[]
    >(jobRequestAttachmentsData)

    const selectedJobRequestLineIds = selectedJobRequestLines.map(
        item => item.id,
    )

    useEffect(() => {
        if (setCheckboxesLocked && !jobRequestAttachments.length) {
            setCheckboxesLocked(false)
        }
    }, [setCheckboxesLocked, jobRequestAttachments.length])

    const useJobRequestAttachment = (file: File) => {
        const fileData = file

        return mutatingOperation.useCreateAttachment(fileData, {
            jobRequestId,
            selectedJobRequestLineIds,
            attachmentType: attachmentTypeData.attachmentProcess.attachmentType,
            attachmentSource,
        })
    }

    const useOnRemove = (ids: string[]) => {
        return mutatingOperation
            .useRemoveJobRequestAttachment(ids, jobRequestId)
            .then(() => {
                message.success(`Removed Successfully`)
                return setJobRequestAttachments(
                    jobRequestAttachments.filter(jobRequestAttachment => {
                        const value = !ids.includes(jobRequestAttachment.id)

                        return value
                    }),
                )
            })
            .catch((e: string) =>
                logger.exception('Remove attachment request failed', e),
            )
    }

    const useOnSuccess = async (file: File) => {
        const fileAttachment = await mutatingOperation.useMarkUploadAttachment(
            file,
            jobRequestId,
        )

        if (!fileAttachment) {
            message.error(`Failed to upload job request attachment`)
            return null
        }

        const jobRequestAttachment = transformFileToJobRequestAttachment(
            fileAttachment,
            attachmentTypeData.attachmentProcess.attachmentType,
            attachmentSource,
            selectedJobRequestLines,
        )

        message.success(`${file.name} uploaded correctly`)

        tempArray.push(...jobRequestAttachment)

        setJobRequestAttachments([...tempArray])
        if (setCheckboxesLocked && tempArray.length > 0) {
            setCheckboxesLocked(true)
        }

        return fileAttachment
    }

    return (
        <Box justifyContent="between" className={styles.uploadUiWrapper}>
            {!(
                isJobCompleted &&
                attachmentSource === ALLOWED_ATTACHMENT_SOURCE_TYPE.MP
            ) && (
                <Box
                    style={{
                        width: '100%',
                        padding: '5px',
                    }}
                >
                    <UploadComponent
                        listType="picture-card"
                        onCreate={useJobRequestAttachment}
                        onSuccess={useOnSuccess}
                        uploadTitle={uploadTitle}
                        multiple={multiple}
                    />
                </Box>
            )}
            <Box
                padding="5px"
                style={{ fontWeight: 300, color: getColor('blackAlpha45') }}
            >
                Supported file types: .jpg .png .csv, .xls, .pdf, .doc{' '}
            </Box>
            <FileList
                files={jobRequestAttachments}
                modalControls={{
                    backFwd: true,
                    rotate: true,
                    zoom: true,
                    canBeDeleted:
                        attachmentSource === ALLOWED_ATTACHMENT_SOURCE_TYPE.MP
                            ? !isJobCompleted
                            : true,
                    newWindow: true,
                }}
                modalCss={styles.docViewerModal}
                modalTitle
                onRemove={useOnRemove}
                typeOfModule={'inventoryQc'}
                showIcons
                attachmentTitle={title}
            />
        </Box>
    )
}
